import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useVendorForm } from './../VendorFormContext';

const Description = () => {
  const navigate = useNavigate();
  const { formData, updateFormData, saveVendorData, saveAllChanges, hasUnsavedChanges, isCreating, isSaving, markTabComplete } = useVendorForm();
  // Ensure defaultValues is always { description: string }
  let descriptionValue = "";
  if (typeof formData?.description === "string") {
    descriptionValue = formData.description;
  } else if (formData?.description && typeof formData.description === "object" && typeof formData.description.description === "string") {
    descriptionValue = formData.description.description;
  }
  const defaultValues = { description: String(descriptionValue ?? '') };

  const { register, handleSubmit, reset, watch, formState: { errors } } = useForm({
    defaultValues,
    mode: 'onChange',
    shouldUnregister: true,
  });

  // Only reset if defaultValues change from outside (not on every keystroke)
  const prevDefaults = useRef(defaultValues);
  useEffect(() => {
    if (JSON.stringify(prevDefaults.current) !== JSON.stringify(defaultValues)) {
      reset(defaultValues);
      prevDefaults.current = defaultValues;
    }
  }, [defaultValues, reset]);

  // Watch for changes and auto-sync to context for global change detection
  // Skip first sync — resetVendorData already populated context
  const watched = watch();
  const prevWatchedData = useRef(watched);
  const isFirstSync = useRef(true);
  useEffect(() => {
    if (JSON.stringify(prevWatchedData.current) !== JSON.stringify(watched)) {
      if (!isFirstSync.current) {
        updateFormData('description', watched);
      }
      prevWatchedData.current = watched;
      isFirstSync.current = false;
    }
  }, [watched, updateFormData]);

  const onSubmit = async (data) => {
    // data: { description: string }
    updateFormData("description", data.description);
    markTabComplete("description"); // Mark tab as completed
    saveVendorData("description", data.description);
    reset({ description: data.description }); // Reset with current data to clear dirty state
  };

  return (
    <div className="w-full h-full">
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col gap-y-6 mt-5 text-sm font-normal text-text-hint"
      >
        <div className="grid grid-cols-4 gap-3">
          <div className="flex flex-col col-span-3 ">
            <label htmlFor="description" className="text-text-hint mb-1">
              Description
            </label>
            <textarea
              {...register('description', { required: 'Description is required', maxLength: { value: 500, message: 'Description must not exceed 500 characters' } })}
              placeholder="Description"
              id="description"
              className="focus:outline-none border rounded-lg p-2"
              rows={6}
              maxLength={500}
            />
            {errors.description && (
              <span className="text-danger-500">{errors.description.message}</span>
            )}
          </div>
        </div>
      </form>
      <div className="w-full py-10 px-8 flex justify-end items-center gap-2">
        <button
          type="button"
          onClick={() => {
            updateFormData("description", watched.description);
            navigate("/sales/vendors/details/billing-terms");
          }}
          className="text-secondary-700 border border-secondary-700 py-2 px-5 rounded-full text-base bg-white hover:bg-gray-50 transition"
        >
          Previous
        </button>
        {isCreating ? (
          <button
            type="submit"
            onClick={handleSubmit(onSubmit)}
            disabled={isSaving}
            className="text-white bg-secondary-700 py-2 px-5 rounded-full text-base"
          >
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        ) : (
          hasUnsavedChanges && (
            <button
              type="button"
              disabled={isSaving}
              onClick={saveAllChanges}
              className="text-white bg-green-600 py-2 px-5 rounded-full text-base hover:bg-green-700 transition disabled:opacity-50"
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          )
        )}
      </div>
    </div>
  );
};

export default Description;
